import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Booking() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    concerns: '',
    message: '',
    guardianName: '',
    guardianPhone: '',
    guardianEmail: '',
    guardianRelation: '',
    isMinor: false,
    parentalConsent: false,
    parentEmail: '',
    termsAgreed: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Load guardian info from localStorage if exists
  useEffect(() => {
    const guardianInfo = localStorage.getItem('guardianInfo');
    if (guardianInfo) {
      try {
        const parsed = JSON.parse(guardianInfo);
        setFormData(prev => ({
          ...prev,
          guardianName: parsed.guardianName || '',
          guardianPhone: parsed.guardianPhone || '',
          guardianEmail: parsed.guardianEmail || '',
          guardianRelation: parsed.guardianRelation || ''
        }));
      } catch (e) {
        console.error('Failed to parse guardian info');
      }
    }
  }, []);

  // Age change handler to detect if user is minor
  const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const age = parseInt(e.target.value);
    const isMinor = age < 18;
    
    setFormData(prev => ({ 
      ...prev, 
      age: e.target.value,
      isMinor,
      parentalConsent: isMinor ? false : true, // Reset consent when age changes
      parentEmail: isMinor ? prev.parentEmail : '' // Clear parent email if not minor
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validation for terms agreement
    if (!formData.termsAgreed) {
      alert('注意事項・免責事項への同意が必要です。');
      setIsSubmitting(false);
      return;
    }

    // Validation for minors
    if (formData.isMinor) {
      if (!formData.parentalConsent) {
        alert('未成年の方は保護者の同意が必要です。');
        setIsSubmitting(false);
        return;
      }
      if (!formData.parentEmail.trim()) {
        alert('未成年の方は保護者のメールアドレスの入力が必要です。');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Submit to Readdy Form first
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== false) {
          formBody.append(key, value.toString());
        }
      });

      const formResponse = await fetch('https://readdy.ai/api/form/d5i64drihlvellpk58qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (!formResponse.ok) {
        throw new Error('Form submission failed');
      }

      // Create Stripe payment session
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const paymentResponse = await fetch(`${supabaseUrl}/functions/v1/create-payment-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          customerEmail: formData.email,
          customerName: formData.name,
          preferredDate: formData.preferredDate,
          preferredTime: formData.preferredTime,
          concerns: formData.concerns,
          isMinor: formData.isMinor,
          parentEmail: formData.isMinor ? formData.parentEmail : null
        })
      });

      if (!paymentResponse.ok) {
        throw new Error('Payment session creation failed');
      }

      const { url } = await paymentResponse.json();

      // Redirect to Stripe checkout
      window.location.href = url;

    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleConsentClick = () => {
    navigate('/consent');
  };

  const hasGuardianInfo = formData.guardianName && formData.guardianPhone && formData.guardianEmail;

  return (
    <section id="booking" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            気軽に予約して、相談してみよう
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            フォームから正式予約・事前決済
          </p>
        </div>

        {/* Booking Guide Button */}
        <div className="mb-12 bg-gradient-to-br from-[#FFD93D]/20 to-[#FF9B8A]/20 rounded-3xl p-8 border-2 border-[#FFD93D]/40">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gradient-to-br from-[#FFD93D] to-[#FF9B8A] rounded-2xl flex items-center justify-center shadow-lg">
                <i className="ri-calendar-check-line text-white text-3xl w-8 h-8 flex items-center justify-center"></i>
              </div>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Klee One', cursive" }}>
                予約方法は？
              </h3>
              <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                フォーム送信前に<strong className="text-[#FF9B8A]">予約可能な日時を選択</strong>してみよう！<br />
                リアルタイムカレンダーで空き時間を確認できます。
              </p>
            </div>

            <div className="flex-shrink-0">
              <button
                onClick={() => navigate('/booking-guide')}
                className="bg-gradient-to-r from-[#FFD93D] to-[#FF9B8A] hover:from-[#FFC82D] hover:to-[#FF8A77] text-white px-8 py-4 rounded-full text-base font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-calendar-line text-xl w-5 h-5 flex items-center justify-center"></i>
                予約方法を見る
              </button>
            </div>
          </div>
        </div>

        {/* Payment Notice */}
        <div className="mb-12 bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-8 border-2 border-purple-200">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                <i className="ri-bank-card-line text-white text-3xl w-8 h-8 flex items-center justify-center"></i>
              </div>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Klee One', cursive" }}>
                予約時に事前決済
              </h3>
              <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                フォーム送信後、<strong className="text-purple-600">クレジットカード決済ページ</strong>へ移動します。<br />
                決済完了後、予約が確定します。
              </p>
            </div>
          </div>
        </div>

        {/* Consent Notice */}
        <div className="mb-12 bg-gradient-to-br from-[#FF9B8A]/10 to-[#FFD93D]/10 rounded-3xl p-8 border-2 border-[#FF9B8A]/30">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gradient-to-br from-[#FF9B8A] to-[#FFD93D] rounded-2xl flex items-center justify-center shadow-lg">
                <i className="ri-file-list-3-line text-white text-3xl w-8 h-8 flex items-center justify-center"></i>
              </div>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Klee One', cursive" }}>
                予約前に必ずご確認ください
              </h3>
              <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-1">
                本サービスは<strong className="text-[#FF9B8A]">医療行為ではありません</strong>。注意事項への同意が必要です。
              </p>
              <p className="text-gray-600 text-sm">
                18歳未満の方は保護者の同意が必須です。予約は下記フォームから行ってください。
              </p>
            </div>

            <div className="flex-shrink-0">
              <button
                onClick={handleConsentClick}
                className="bg-gradient-to-r from-[#FF9B8A] to-[#FFD93D] hover:from-[#FF8A77] hover:to-[#FFC82D] text-white px-8 py-4 rounded-full text-base font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-checkbox-circle-line text-xl w-5 h-5 flex items-center justify-center"></i>
                同意確認へ
              </button>
            </div>
          </div>
        </div>

        {/* Booking Form */}
        <form onSubmit={handleSubmit} data-readdy-form className="bg-[#FFF8F3] rounded-3xl p-8 md:p-12 shadow-lg border-2 border-[#FF9B8A]/30">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Klee One', cursive" }}>
              予約フォーム
            </h3>
            <p className="text-gray-700 text-sm md:text-base">
              同意確認後、こちらのフォームから正式に予約してください
            </p>
          </div>

          {/* Guardian Info Display (if exists) */}
          {hasGuardianInfo && (
            <div className="mb-8 bg-yellow-50 border border-yellow-200 rounded-xl p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-parent-line text-yellow-700 text-lg w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-yellow-900 mb-1">保護者情報が登録されています</h4>
                  <p className="text-sm text-yellow-800">
                    保護者名: <strong>{formData.guardianName}</strong> ({formData.guardianRelation})<br />
                    メールアドレス: <strong>{formData.guardianEmail}</strong><br />
                    電話番号: <strong>{formData.guardianPhone}</strong>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleConsentClick}
                className="text-sm text-yellow-700 hover:text-yellow-900 font-medium cursor-pointer"
              >
                変更する →
              </button>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
                placeholder="山田 太郎"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                年齢 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleAgeChange}
                required
                min="1"
                max="100"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
                placeholder="16"
              />
            </div>
          </div>

          {/* Minor Consent Section */}
          {formData.isMinor && (
            <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-parent-line text-yellow-700 text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-yellow-900 mb-2">未成年者の保護者同意</h4>
                  <p className="text-sm text-yellow-800 mb-4">
                    18歳未満の方がサービスをご利用される場合、保護者の同意が必要です。
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    保護者のメールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="parentEmail"
                    value={formData.parentEmail}
                    onChange={handleChange}
                    required={formData.isMinor}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FFD93D] text-sm"
                    placeholder="parent@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ※ 保護者の方に確認メールをお送りします
                  </p>
                </div>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      name="parentalConsent"
                      checked={formData.parentalConsent}
                      onChange={handleChange}
                      required={formData.isMinor}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      formData.parentalConsent 
                        ? 'bg-[#FFD93D] border-[#FFD93D]' 
                        : 'border-gray-300 group-hover:border-[#FFD93D]'
                    }`}>
                      {formData.parentalConsent && (
                        <i className="ri-check-line text-white text-sm w-3 h-3 flex items-center justify-center"></i>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      保護者の同意を得ています <span className="text-red-500">*</span>
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      保護者の方がこのサービスの利用に同意していることを確認してください
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
                placeholder="example@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                電話番号
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
                placeholder="090-1234-5678"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                希望日 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                希望時間帯 <span className="text-red-500">*</span>
              </label>
              <select
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="morning">午前（9:00-12:00）</option>
                <option value="afternoon">午後（13:00-17:00）</option>
                <option value="evening">夕方（17:00-20:00）</option>
                <option value="night">夜（20:00-22:00）</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              相談したい内容 <span className="text-red-500">*</span>
            </label>
            <select
              name="concerns"
              value={formData.concerns}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm cursor-pointer"
            >
              <option value="">選択してください</option>
              <option value="love">恋愛の悩み</option>
              <option value="body">体の変化・悩み</option>
              <option value="eating">食べ方の悩み</option>
              <option value="ocd">一つの考えに縛られる</option>
              <option value="parents">親との関係</option>
              <option value="daily">日常のモヤモヤ</option>
              <option value="other">その他</option>
            </select>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              メッセージ（任意）
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm resize-none"
              placeholder="事前に伝えておきたいことがあれば、お気軽にお書きください（500文字まで）"
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {formData.message.length}/500
            </div>
          </div>

          {/* Terms Agreement Section */}
          <div className="mb-8 bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-file-list-3-line text-gray-700 text-xl w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-2">注意事項・免責事項への同意</h4>
                <p className="text-sm text-gray-700 mb-4">
                  ご予約の前に、必ず注意事項をご確認いただき、同意してください。
                </p>
              </div>
            </div>

            <div className="mb-4">
              <button
                type="button"
                onClick={() => navigate('/terms')}
                className="text-[#FF9B8A] hover:text-[#FF8A77] text-sm font-medium flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-external-link-line text-base w-4 h-4 flex items-center justify-center"></i>
                注意事項・免責事項を確認する
              </button>
            </div>

            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  name="termsAgreed"
                  checked={formData.termsAgreed}
                  onChange={handleChange}
                  required
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  formData.termsAgreed 
                    ? 'bg-[#FF9B8A] border-[#FF9B8A]' 
                    : 'border-gray-300 group-hover:border-[#FF9B8A]'
                }`}>
                  {formData.termsAgreed && (
                    <i className="ri-check-line text-white text-sm w-3 h-3 flex items-center justify-center"></i>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  注意事項・免責事項を確認し、同意します <span className="text-red-500">*</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  本サービスが医療行為ではないこと、および利用は自己責任であることを理解しました
                </p>
              </div>
            </label>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm">
              予約リクエストを受け付けました。確認のメールをお送りしますので、しばらくお待ちください。
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm">
              送信に失敗しました。もう一度お試しください。
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#6B4E3D] hover:bg-[#5A3F2E] text-white px-8 py-4 rounded-full text-base font-medium transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? '決済ページへ移動中...' : '予約して決済へ進む →'}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            ※ 決済完了後、Google Meetのリンクをメールでお送りします
          </p>
        </form>
      </div>
    </section>
  );
}
